import type { Bridge } from '@casa/bridge';

// The dashboards kept on this device (docs/specs/settings.md, ST-5): one file each, under
// `dashboards/`, by name. Names are those of the Tilez Hub ("Kitchen tablet"), which storage paths
// cannot hold as they are (no spaces), so each character other than a letter, a digit, "." or "-"
// is written as "_" and its code in hex: "Kitchen tablet" is `dashboards/Kitchen_20tablet.json`.
// Names that differ only in case are the same dashboard, as file systems on Windows and macOS see it.

const PREFIX = 'dashboards/';
const EXTENSION = '.json';

/** Names as people give them: letters, digits, spaces, ".", "_" and "-", at most 64, as on the server. */
const NAME_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9 ._-]{0,62}[A-Za-z0-9_-])?$/;

export function isDashboardName(name: string) : boolean {
    return NAME_PATTERN.test(name);
}

/** The storage path of a dashboard on this device. */
export function deviceFileOf(name: string) : string {
    const encoded = [...name].map((character) => (/[A-Za-z0-9.-]/.test(character) ? character : `_${character.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`)).join('');

    return `${PREFIX}${encoded}${EXTENSION}`;
}

/** The name a storage path holds; undefined for a path that is not a dashboard's. */
export function nameOfDeviceFile(path: string) : string | undefined {
    if (!path.startsWith(PREFIX) || !path.endsWith(EXTENSION) || path.slice(PREFIX.length).includes('/')) {
        return undefined;
    }

    const name = path.slice(PREFIX.length, -EXTENSION.length).replace(/_([0-9A-F]{2})/g, (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)));

    return isDashboardName(name) ? name : undefined;
}

export async function listDeviceDashboards(bridge: Bridge) : Promise<string[]> {
    const paths = await bridge.storage.list(PREFIX);

    return paths.flatMap((path) => nameOfDeviceFile(path) ?? []).sort((a, b) => a.localeCompare(b));
}

/** The name as this device keeps it, when one that differs only in case is there; undefined when there is none. */
export async function findDeviceDashboard(bridge: Bridge, name: string) : Promise<string | undefined> {
    return (await listDeviceDashboards(bridge)).find((existing) => existing.toLowerCase() === name.toLowerCase());
}

export async function readDeviceDashboard(bridge: Bridge, name: string) : Promise<string | undefined> {
    const stored = await bridge.storage.read(deviceFileOf(name));

    return stored ? new TextDecoder().decode(stored) : undefined;
}

export async function writeDeviceDashboard(bridge: Bridge, name: string, text: string) : Promise<void> {
    await bridge.storage.write(deviceFileOf(name), new TextEncoder().encode(text));
}

export async function removeDeviceDashboard(bridge: Bridge, name: string) : Promise<void> {
    await bridge.storage.remove(deviceFileOf(name));
}

/** A name not yet used on this device: `base`, or `base 2`, `base 3`… */
export async function freeDeviceName(bridge: Bridge, base: string) : Promise<string> {
    const taken = new Set((await listDeviceDashboards(bridge)).map((name) => name.toLowerCase()));
    if (!taken.has(base.toLowerCase())) {
        return base;
    }

    for (let number = 2; ; number++) {
        const candidate = `${base.slice(0, 60)} ${number}`;
        if (!taken.has(candidate.toLowerCase())) {
            return candidate;
        }
    }
}
